import React, { useState, useEffect } from 'react';
import Section from "../../Section";
import Alert from "../../Alert";
import Table from "../../Table";
import Form from "../../Form";
import { useAuth } from "../../../helpers/AuthContext";

export default function ChannelAdminsManager() {
    const { fetch, API_URL } = useAuth();

    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [addEmail, setAddEmail] = useState('');
    const [addError, setAddError] = useState(null);

    const showSuccess = (msg) => {
        setSuccess(msg);
        setTimeout(() => setSuccess(null), 3000);
    };

    const loadAdmins = () => {
        return fetch(API_URL + '/v1/seller/admins')
            .then(res => res.json())
            .then(data => {
                if (data?.meta?.success) {
                    setAdmins(data.data || []);
                } else {
                    setError((data.meta && data.meta.message) || data.error || 'Failed to load admins.');
                }
            })
            .catch(() => setError('Failed to load admins.'))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        loadAdmins();
    }, []);

    const handleAdd = () => {
        if (submitting) return;
        setSubmitting(true);
        setAddError(null);

        fetch(API_URL + '/v1/seller/admins', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: addEmail }),
        })
            .then(res => res.json())
            .then(data => {
                if (data?.meta?.success) {
                    loadAdmins();
                    setShowAddForm(false);
                    setAddEmail('');
                    showSuccess('Admin added successfully.');
                } else {
                    setAddError((data.meta && data.meta.message) || data.error || 'Failed to add admin.');
                }
            })
            .catch(() => setAddError('An error occurred while adding admin.'))
            .finally(() => setSubmitting(false));
    };

    const handleRemove = (id) => {
        if (submitting) return;
        setSubmitting(true);
        setError(null);

        fetch(API_URL + `/v1/seller/admins/${id}`, { method: 'DELETE' })
            .then(res => res.json())
            .then(data => {
                if (data?.meta?.success) {
                    setAdmins(prev => prev.filter(a => a.id !== id));
                    showSuccess('Admin removed successfully.');
                } else {
                    setError((data.meta && data.meta.message) || data.error || 'Failed to remove admin.');
                }
            })
            .catch(() => setError('An error occurred while removing admin.'))
            .finally(() => setSubmitting(false));
    };

    const columns = [
        { key: 'name', header: 'Name', render: (value, row) => row.Account?.name },
        { key: 'email', header: 'Email', render: (value, row) => row.Account?.email },
        { key: 'role', header: 'Role', render: (value, row) => row.role || '—' },
        {
            key: 'actions',
            header: 'Actions',
            sortable: false,
            render: (value, row) => (
                <button
                    className="button danger small"
                    disabled={submitting}
                    onClick={() => handleRemove(row.id)}
                >
                    Remove
                </button>
            ),
        },
    ];

    return (
        <Section title="Channel Admins" id="channel-admins-section">
            {error && <Alert type="error" style={{ marginBottom: 16 }}>{error}</Alert>}
            {success && <Alert type="success" style={{ marginBottom: 16 }}>{success}</Alert>}

            {loading && <p>Loading...</p>}

            {!loading && admins.length === 0 && (
                <p><em>No co-admins yet. Add one below.</em></p>
            )}

            {!loading && admins.length > 0 && (
                <Table
                    data={admins}
                    columns={columns}
                    keyField="id"
                    sortable={true}
                    pagination={false}
                />
            )}

            <div style={{ marginTop: 16 }}>
                <button
                    className="button"
                    onClick={() => setShowAddForm(prev => !prev)}
                >
                    {showAddForm ? 'Cancel' : 'Add Admin'}
                </button>
            </div>

            {showAddForm && (
                <div style={{ marginTop: 16 }}>
                    <Form
                        values={{ email: addEmail }}
                        onChange={(vals) => setAddEmail(vals.email)}
                        onSubmit={handleAdd}
                        fields={[
                            {
                                type: 'email',
                                name: 'email',
                                label: 'Email Address',
                                placeholder: 'Enter email address',
                                required: true,
                            },
                            {
                                type: 'submit',
                                label: submitting ? 'Adding...' : 'Add Admin',
                                className: 'btn btn-primary',
                                disabled: submitting,
                            },
                        ]}
                    />
                    <p className="help"><small>The user must already have a Mayhem Marketplace account (either seller or shopper).</small></p>
                    {addError && <Alert type="error" style={{ marginTop: 8 }}>{addError}</Alert>}
                    <button
                        className="btn btn-secondary"
                        style={{ marginTop: 8 }}
                        onClick={() => { setShowAddForm(false); setAddEmail(''); setAddError(null); }}
                    >
                        Cancel
                    </button>
                </div>
            )}
        </Section>
    );
}
